/**
 * SubmitScreen - Premium form matching v0 submit screen design
 */

import { useCallback, useState, useRef, useEffect } from 'react';
import type {
  Agent,
  AgentCategory,
  CreateAgentRequest,
  CreateAgentResponse,
} from '../types/agent';
import { CATEGORIES } from '../types/agent';
import { hapticFeedback, hapticNotification } from '../lib/telegram';
import { ApiError } from '../lib/api';
import { createAgent, updateAgent, uploadLogo } from '../lib/api';

interface SubmitScreenProps {
  onBack: () => void;
  onSuccess: (agentId: string) => void;
  agentId?: string;
  existingAgent?: Agent;
}

interface FormData {
  name: string;
  description: string;
  url: string;
  category: AgentCategory | '';
  tags: string;
  logo: string;
  telegram_bot: string;
  twitter: string;
  ton_wallet: string;
}

interface FormErrors {
  name?: string;
  description?: string;
  url?: string;
  category?: string;
  tags?: string;
  logo?: string;
  telegram_bot?: string;
  twitter?: string;
  ton_wallet?: string;
}

const initialFormData: FormData = {
  name: '',
  description: '',
  url: '',
  category: '',
  tags: '',
  logo: '',
  telegram_bot: '',
  twitter: '',
  ton_wallet: '',
};

function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

function isValidTonWallet(address: string): boolean {
  return /^[EU]Q[A-Za-z0-9_-]{46}$/.test(address);
}

export function SubmitScreen({ onBack: _onBack, onSuccess, agentId, existingAgent }: SubmitScreenProps) {
  const isEditing = Boolean(agentId && existingAgent);

  const getInitialFormData = useCallback((): FormData => {
    if (existingAgent) {
      return {
        name: existingAgent.name || '',
        description: existingAgent.description || '',
        url: existingAgent.url || '',
        category: (existingAgent.category as AgentCategory) || '',
        tags: existingAgent.tags?.join(', ') || '',
        logo: existingAgent.logo || '',
        telegram_bot: existingAgent.telegram_bot || '',
        twitter: existingAgent.twitter || '',
        ton_wallet: existingAgent.ton_wallet || '',
      };
    }
    return initialFormData;
  }, [existingAgent]);

  const [formData, setFormData] = useState<FormData>(getInitialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showOptional, setShowOptional] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateAgentResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(existingAgent?.logo || null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFormData(getInitialFormData());
    setLogoPreview(existingAgent?.logo || null);
    setSelectedFile(null);
    setErrors({});
    setSubmitError(null);
    setResult(null);
  }, [existingAgent, getInitialFormData]);

  const updateField = useCallback((field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setSubmitError(null);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, logo: 'Only PNG, JPG, and WebP images are allowed' }));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, logo: 'Image must be under 2MB' }));
      return;
    }
    setSelectedFile(file);
    setErrors((prev) => ({ ...prev, logo: undefined }));
    const reader = new FileReader();
    reader.onload = (event) => { setLogoPreview(event.target?.result as string); };
    reader.readAsDataURL(file);
    setFormData((prev) => ({ ...prev, logo: '' }));
  }, []);

  const handleRemoveLogo = useCallback(() => {
    setSelectedFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    else if (formData.name.trim().length < 2) newErrors.name = 'Name must be at least 2 characters';
    else if (formData.name.trim().length > 50) newErrors.name = 'Name must be under 50 characters';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    else if (formData.description.trim().length < 10) newErrors.description = 'Description must be at least 10 characters';
    else if (formData.description.trim().length > 500) newErrors.description = 'Description must be under 500 characters';
    if (!formData.url.trim()) newErrors.url = 'URL is required';
    else if (!isValidUrl(formData.url.trim())) newErrors.url = 'Please enter a valid URL (https://...)';
    if (!formData.category) newErrors.category = 'Please select a category';
    if (formData.logo.trim() && !isValidUrl(formData.logo.trim())) newErrors.logo = 'Please enter a valid logo URL';
    if (formData.telegram_bot.trim()) {
      const bot = formData.telegram_bot.trim().replace(/^@/, '');
      if (!/^[A-Za-z0-9_]{5,32}$/.test(bot)) newErrors.telegram_bot = 'Invalid bot username';
    }
    if (formData.twitter.trim()) {
      const handle = formData.twitter.trim().replace(/^@/, '');
      if (!/^[A-Za-z0-9_]{1,15}$/.test(handle)) newErrors.twitter = 'Invalid Twitter handle';
    }
    if (formData.ton_wallet.trim() && !isValidTonWallet(formData.ton_wallet.trim())) newErrors.ton_wallet = 'Invalid TON wallet address';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const parseTags = useCallback((tagString: string): string[] => {
    return tagString.split(',').map((tag) => tag.trim().toLowerCase()).filter((tag) => tag.length > 0 && tag.length <= 20).slice(0, 5);
  }, []);

  const handleSubmit = useCallback(async () => {
    hapticFeedback('medium');
    if (!validate()) { hapticNotification('error'); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      let logoUrl = formData.logo.trim();
      if (selectedFile) {
        setUploadingLogo(true);
        try {
          const uploadResult = await uploadLogo(selectedFile);
          logoUrl = uploadResult.url;
        } catch (err) {
          console.error('Logo upload failed:', err);
          if (err instanceof ApiError) setSubmitError(`Logo upload failed: ${err.message}`);
          else setSubmitError('Failed to upload logo. Please try again.');
          hapticNotification('error');
          setSubmitting(false);
          setUploadingLogo(false);
          return;
        }
        setUploadingLogo(false);
      }
      const payload: CreateAgentRequest = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        url: formData.url.trim(),
        category: formData.category as AgentCategory,
        tags: parseTags(formData.tags),
      };
      if (logoUrl) payload.logo = logoUrl;
      if (formData.telegram_bot.trim()) payload.telegram_bot = formData.telegram_bot.trim().replace(/^@/, '');
      if (formData.twitter.trim()) payload.twitter = formData.twitter.trim().replace(/^@/, '');
      if (formData.ton_wallet.trim()) payload.ton_wallet = formData.ton_wallet.trim();
      if (isEditing && agentId) {
        await updateAgent(agentId, payload);
        hapticNotification('success');
        onSuccess(agentId);
        return;
      } else {
        const response = await createAgent(payload);
        setResult(response);
        hapticNotification('success');
      }
    } catch (err) {
      console.error('Submit failed:', err);
      if (err instanceof ApiError) setSubmitError(err.message);
      else setSubmitError('Failed to submit. Please try again.');
      hapticNotification('error');
    } finally {
      setSubmitting(false);
    }
  }, [formData, validate, parseTags, selectedFile]);

  const handleCopyShareUrl = useCallback(async () => {
    if (!result?.shareUrl) return;
    hapticFeedback('light');
    try {
      await navigator.clipboard.writeText(result.shareUrl);
      setCopied(true);
      hapticNotification('success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      hapticNotification('error');
    }
  }, [result?.shareUrl]);

  const handleViewListing = useCallback(() => {
    if (!result?.agentId) return;
    hapticFeedback('light');
    onSuccess(result.agentId);
  }, [result?.agentId, onSuccess]);

  // Success state
  if (result) {
    return (
      <div className="text-center py-8">
        <p className="text-6xl mb-4">🎉</p>
        <h2 className="text-2xl font-bold text-white mb-2">App Listed!</h2>
        <p className="text-[#a0a0a0] mb-6">
          Your app has been successfully added to TeleClaw.
        </p>
        <div className="bg-[#242424] border border-white/5 rounded-xl p-4 mb-6">
          <p className="text-sm text-[#a0a0a0] mb-2">Share Link</p>
          <p className="text-sm text-white break-all mb-3">{result.shareUrl}</p>
          <button
            onClick={handleCopyShareUrl}
            className="px-4 py-2 bg-[#0098EA] text-white rounded-lg font-medium text-sm"
          >
            {copied ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>
        <div className="space-y-3">
          <button
            onClick={handleViewListing}
            className="w-full px-6 py-3 bg-[#0098EA] text-white rounded-xl font-medium"
          >
            View Listing
          </button>
        </div>
      </div>
    );
  }

  const categoryOptions = CATEGORIES.filter((c) => c.value !== 'all');

  const inputClass = (hasError: boolean) => `
    w-full px-4 py-3 rounded-xl
    bg-[#242424] border border-white/5
    text-white placeholder-[#a0a0a0]
    transition-colors outline-none
    ${hasError ? 'border-red-500' : 'focus:border-[#0098EA]/50'}
  `;

  return (
    <div className="pb-6 animate-screen-fade">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">
          {isEditing ? 'Edit Your Agent' : 'Submit Your Agent'}
        </h1>
        <p className="text-[#a0a0a0]">
          {isEditing ? 'Update your app\'s information' : 'Share your AI agent with the community'}
        </p>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Enter agent name"
            className={inputClass(!!errors.name)}
          />
          {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Description <span className="text-red-400">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Describe what your agent does..."
            rows={3}
            className={`${inputClass(!!errors.description)} min-h-[100px] resize-none`}
          />
          {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
        </div>

        {/* URL */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            App URL <span className="text-red-400">*</span>
          </label>
          <input
            type="url"
            value={formData.url}
            onChange={(e) => updateField('url', e.target.value)}
            placeholder="https://your-agent.com"
            className={inputClass(!!errors.url)}
          />
          {errors.url && <p className="text-red-400 text-sm mt-1">{errors.url}</p>}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Category <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <select
              value={formData.category}
              onChange={(e) => updateField('category', e.target.value)}
              className={`${inputClass(!!errors.category)} pr-10 appearance-none`}
            >
              <option value="" className="bg-[#242424]">Select a category</option>
              {categoryOptions.map((cat) => (
                <option key={cat.value} value={cat.value} className="bg-[#242424]">{cat.label}</option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-[#a0a0a0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {errors.category && <p className="text-red-400 text-sm mt-1">{errors.category}</p>}
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Tags <span className="text-[#a0a0a0]">(comma-separated, max 5)</span>
          </label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => updateField('tags', e.target.value)}
            placeholder="ai, trading, bot"
            className={inputClass(false)}
          />
        </div>

        {/* Add More Details Toggle */}
        <button
          type="button"
          onClick={() => setShowOptional(!showOptional)}
          className="w-full text-[#0098EA] hover:bg-[#0098EA]/5 h-11 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
        >
          <span>+ Add more details</span>
          <svg
            className={`w-4 h-4 transition-transform ${showOptional ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Optional Fields */}
        {showOptional && (
          <div className="space-y-4 animate-fade-in-up">
            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Logo</label>
              {logoPreview && (
                <div className="relative inline-block mb-3">
                  <img src={logoPreview} alt="Logo preview" className="w-20 h-20 rounded-full object-cover border-2 border-[#0098EA]" />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-sm flex items-center justify-center"
                  >
                    ✕
                  </button>
                </div>
              )}
              {!logoPreview && (
                <div className="relative">
                  <input type="file" accept="image/*" className="hidden" id="logo-upload" ref={fileInputRef} onChange={handleFileSelect} />
                  <label
                    htmlFor="logo-upload"
                    className="flex items-center justify-center gap-2 bg-[#242424] border border-dashed border-white/10 text-[#a0a0a0] h-24 rounded-xl cursor-pointer hover:border-[#0098EA]/50 hover:bg-[#242424]/50 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <span>Upload logo</span>
                  </label>
                </div>
              )}
              {!selectedFile && (
                <div className="mt-3">
                  <p className="text-xs text-[#a0a0a0] mb-1">Or enter URL:</p>
                  <input
                    type="url"
                    value={formData.logo}
                    onChange={(e) => updateField('logo', e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className={`${inputClass(!!errors.logo)} text-sm`}
                  />
                </div>
              )}
              {errors.logo && <p className="text-red-400 text-sm mt-1">{errors.logo}</p>}
            </div>

            {/* Telegram Bot */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Telegram Bot</label>
              <input
                type="text"
                value={formData.telegram_bot}
                onChange={(e) => updateField('telegram_bot', e.target.value)}
                placeholder="@your_bot"
                className={inputClass(!!errors.telegram_bot)}
              />
              {errors.telegram_bot && <p className="text-red-400 text-sm mt-1">{errors.telegram_bot}</p>}
            </div>

            {/* Twitter */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Twitter</label>
              <input
                type="text"
                value={formData.twitter}
                onChange={(e) => updateField('twitter', e.target.value)}
                placeholder="@youragent"
                className={inputClass(!!errors.twitter)}
              />
              {errors.twitter && <p className="text-red-400 text-sm mt-1">{errors.twitter}</p>}
            </div>

            {/* TON Wallet */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">TON Wallet</label>
              <input
                type="text"
                value={formData.ton_wallet}
                onChange={(e) => updateField('ton_wallet', e.target.value)}
                placeholder="EQ..."
                className={inputClass(!!errors.ton_wallet)}
              />
              {errors.ton_wallet && <p className="text-red-400 text-sm mt-1">{errors.ton_wallet}</p>}
            </div>
          </div>
        )}

        {/* Submit error */}
        {submitError && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-center">
            <p className="text-red-400">{submitError}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className={`
            w-full bg-[#0098EA] hover:bg-[#0098EA]/90 text-white h-14 rounded-xl
            font-semibold text-base shadow-lg active:scale-[0.98] transition-transform mt-4
            ${submitting ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {submitting
            ? uploadingLogo ? 'Uploading logo...' : isEditing ? 'Saving...' : 'Submitting...'
            : isEditing ? 'Save Changes' : 'Submit Agent'}
        </button>
      </div>
    </div>
  );
}
